import { webAppPitch } from '@assets/png/imagesPng';
import LocalImage from '@atoms/LocalImage';
import { Text, View } from '@components/primitives';
import config from '@config';
import WelcomCards from '@molecules/Welcome';
import colors from '@theme/colors';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableOpacity } from 'react-native';
import styles from './styles';

function shouldRenderPlayStoreTile(handleInstallApp: () => void, show: boolean) {
  if (show) {
    return (
      <TouchableOpacity onPress={handleInstallApp} style={styles.playButton}>
        <LocalImage
          source={{
            uri: 'https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg',
          }}
          style={styles.playImage}
        />
      </TouchableOpacity>
    );
  }
  return null;
}

function shouldRenderWebAppTile(launchPWA: () => void, show: boolean) {
  if (show) {
    return (
      <TouchableOpacity onPress={launchPWA} style={styles.playButton}>
        <LocalImage source={webAppPitch} style={styles.webappPitchStyle} />
      </TouchableOpacity>
    );
  }
}

const DeepLinkRedirect = () => {
  const [platform, setPlatform] = useState<'android-chrome' | 'android-other' | 'ios' | 'desktop'>('');
  const { t } = useTranslation('common');

  // Extract URL and link params
  const url = new URL(window.location.href);
  const rawLink = url.searchParams.get('link');

  // Collect all params into one unified object
  const allParams: Record<string, string> = {};

  // 1️⃣ Capture top-level UTM params from outer URL
  url.searchParams.forEach((value, key) => {
    allParams[key] = value;
  });

  // 2️⃣ If nested link (like gajigesa://...) exists → decode & extract its params too
  if (rawLink) {
    try {
      const decodedLink = decodeURIComponent(rawLink);
      const innerUrl = new URL(decodedLink);
      innerUrl.searchParams.forEach((value, key) => {
        allParams[key] = value;
      });
    } catch (err) {
      //   console.warn('Invalid inner link', err);
    }
  }

  // 3️⃣ Clean out duplicate 'link' so it doesn’t get doubled up
  delete allParams.link;
  // 4️⃣ Convert back to URLSearchParams for downstream use
  const utmParams = new URLSearchParams(allParams);

  // 🔥 Rebuild a deep link that *includes* UTM params
  const baseDeepLink = rawLink
    ? rawLink.split('?')[0] // remove old query
    : 'gajigesa://gajigesanative.page.link/referral';
  const deepLinkWithUTM = `${baseDeepLink}?${utmParams.toString()}`;

  // ---- Construct Play Store Fallback ----
  // Include utm_* + deep link in referrer
  const referrerParams = new URLSearchParams({
    ...Object.fromEntries(utmParams.entries()),
    link: rawLink || '', // include deep link itself
  });

  const isSafeLink = rawLink && (rawLink.startsWith('https://') || rawLink.startsWith('gajigesa://'));
  const playStoreUrl = `https://play.google.com/store/apps/details?id=com.gajigesanative&referrer=${encodeURIComponent(
    referrerParams.toString(),
  )}`;

  const pwaLandingUrl = `${config.BASE_PATH}/?${utmParams.toString()}`;

  const intentBase = rawLink
    ? `intent://${deepLinkWithUTM
        .replace(/^https?:\/\//, '')
        .replace(/^gajigesa:\/\//, '')}#Intent;scheme=gajigesa;package=${
        config.APP_PACKAGE
      };S.browser_fallback_url=${encodeURIComponent(playStoreUrl)};end;`
    : '';

  // Platform detection
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();

    if (/iphone|ipad|ipod/.test(userAgent)) {
      setPlatform('ios');
    } else if (/android/.test(userAgent)) {
      if (/chrome\/\d+/.test(userAgent) && !/(oppo|realme|heytap|vivo|samsung)/.test(userAgent)) {
        setPlatform('android-chrome');
      } else {
        setPlatform('android-other'); // OEM browsers fallback
      }
    } else {
      setPlatform('desktop');
    }
  }, []);

  useEffect(() => {
    if (!isSafeLink) {
      window.location.href = pwaLandingUrl;
      return;
    }
    // Android Chrome: use intent:// to open app or fallback to Play Store
    if (platform === 'android-chrome') {
      window.location.href = intentBase;
    }

    if (platform === 'android-other') {
      const schemeLink = deepLinkWithUTM.replace(/^https?:\/\//, 'gajigesa://');
      window.location.href = schemeLink;
      setTimeout(() => {
        window.location.href = playStoreUrl;
      }, 1200);
    }

    // Android other browsers → show CTA manually
  }, [platform, isSafeLink, intentBase]);

  const handleInstallApp = () => {
    if (rawLink) {
      // Try opening the app via custom scheme

      window.location.href = intentBase;
      // Fallback to Play Store if not installed
      setTimeout(() => {
        window.location.href = playStoreUrl;
      }, 1500);
    } else {
      window.location.href = playStoreUrl;
    }
  };

  const launchPWA = () => {
    window.location.href = pwaLandingUrl;
  };

  // For non-Chrome Android browsers → show fallback CTA

  return (
    <View style={styles.deepLinkParent}>
      <WelcomCards
        isKeyboardOpen={false}
        item={{
          cardColor: colors.secondary.orchid,
          textContext: t('login.loginHelpText'),
        }}
      />
      <View style={styles.bottomHalfParent}>
        <Text alignSelf="center" variant="lg-bold" marginTop={8} textAlign="center" marginX="16px">
          {t('common.heroText')}
        </Text>

        <View justifyContent="center" marginTop={16}>
          {shouldRenderPlayStoreTile(handleInstallApp, platform === 'android-chrome' || platform === 'android-other')}

          {shouldRenderWebAppTile(launchPWA, platform === 'ios' || platform === 'desktop')}

          <Text alignSelf="center" marginY={16} variant="md-bold" textAlign="center">
            {t('common.installApp')}
          </Text>
        </View>
      </View>
    </View>
  );
};

export default DeepLinkRedirect;
